import type { QNotifyCreateOptions } from 'quasar'
import { Notify } from 'quasar'
import { symRoundedClose } from '@quasar/extras/material-symbols-rounded'

/**
 * Standardized notification types mapping to Quasar colors/classes.
 *
 * @public
 */
export enum ToastType {
  /** Green 'positive' color */
  Success = 'positive',
  /** Yellow/Orange 'warning' color */
  Warning = 'warning',
  /** Red 'negative' color */
  Error = 'negative',
  /** Blue 'info' color */
  Info = 'info',
  /** Accent color, usually used for ongoing processes */
  Loading = 'ongoing',
}

/**
 * Extended options for Toast notifications.
 * Inherits ALL standard Quasar Notify options (caption, html, icon, classes, etc.)
 * except 'type' and 'message' which are handled by the helper arguments.
 *
 * @public
 */
export interface ToastOptions extends Omit<QNotifyCreateOptions, 'type' | 'message'> {
  /**
   * Optional payload to log to the console (useful for errors/warnings).
   * If provided, it will trigger console.error, console.warn, or console.log depending on the type.
   */
  log?: unknown
}

/**
 * Default configuration for all toasts.
 * Can be overridden on a per-call basis.
 *
 * @internal
 */
const DEFAULT_OPTS: Partial<QNotifyCreateOptions> = {
  position: 'top',
  timeout: 5000,
  progress: true,
  actions: [
    {
      icon: symRoundedClose,
      color: 'white',
      round: true,
      handler: () => {
        /* dismiss */
      },
    },
  ],
}

/**
 * Internal helper to trigger the raw Quasar notification.
 * Merges defaults -> specific type -> user overrides.
 *
 * @param message - The text to display
 * @param type - The ToastType enum value
 * @param options - User provided options
 * @internal
 */
const trigger = (message: string, type: ToastType, options: ToastOptions = {}) => {
  const { log, ...quasarOptions } = options

  // Handle side-effect logging if requested
  if (log) {
    if (type === ToastType.Error) console.error('[Toast Error]:', log)
    else if (type === ToastType.Warning) console.warn('[Toast Warning]:', log)
    else console.log('[Toast Log]:', log)
  }

  Notify.create({
    ...DEFAULT_OPTS,
    type,
    message,
    ...quasarOptions, // Prioritize user-provided options over defaults
  })
}

/**
 * Unified notification helper object containing methods for all toast types.
 *
 * @public
 */
export const toast = {
  /**
   * Display a success (green) notification.
   *
   * @param message - The main text to display
   * @param options - Full Quasar Notify options (caption, timeout, actions, etc.)
   * @public
   */
  success: (message: string, options?: ToastOptions) => trigger(message, ToastType.Success, options),

  /**
   * Display an error (red) notification.
   *
   * @param message - The main text to display
   * @param options - Full Quasar Notify options + optional `log` property
   * @public
   */
  error: (message: string, options?: ToastOptions) => trigger(message, ToastType.Error, options),

  /**
   * Display a warning (orange/yellow) notification.
   *
   * @param message - The main text to display
   * @param options - Full Quasar Notify options + optional `log` property
   * @public
   */
  warning: (message: string, options?: ToastOptions) => trigger(message, ToastType.Warning, options),

  /**
   * Display an info (blue) notification.
   *
   * @param message - The main text to display
   * @param options - Full Quasar Notify options
   * @public
   */
  info: (message: string, options?: ToastOptions) => trigger(message, ToastType.Info, options),

  /**
   * Display a loading notification with a spinner.
   * Defaults to 0 timeout (infinite) unless overridden.
   *
   * @param message - The main text to display
   * @param options - Full Quasar Notify options
   * @public
   */
  loading: (message: string, options?: ToastOptions) => {
    trigger(message, ToastType.Loading, {
      timeout: 0, // Loading usually persists until dismissed or cleared
      spinner: true,
      ...options,
    })
  },
}

// ----------------------------------------------------------------------
// Standalone Exports
// ----------------------------------------------------------------------



/**
 * Displays a success notification.
 * @see {@link toast.success}
 * @public
 */
export const showSuccessNotification = toast.success

/**
 * Displays an error notification.
 * @see {@link toast.error}
 * @public
 */
export const showErrorNotification = toast.error

/**
 * Displays a warning notification.
 * @see {@link toast.warning}
 * @public
 */
export const showWarningNotification = toast.warning

/**
 * Displays an info notification.
 * @see {@link toast.info}
 * @public
 */
export const showInfoNotification = toast.info

/**
 * Displays a loading notification.
 * @see {@link toast.loading}
 * @public
 */
export const showLoadingNotification = toast.loading
